import { z } from 'zod'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { csvDocument, csvLine, parseCsvRows } from '../csv'
import { factoryInputSchema } from './factories'
import { CURRENCIES } from './simulation'

/**
 * 공장 설비·공정별 단가·최소 주문량 틀 (로드맵 8번, 2026-10-30 대표 지시). 공장 목록(factories.capabilities)에 붙는다.
 * 공장이 콜드메일 회신을 이 틀로 보내게 한다 — 초대 링크 웹 폼(apps/share /factory-reply/:token)과 CSV 양식이 같은 틀이다.
 * 샘플 방식(무료·유료 시제품 등)은 이 틀에 두지 않는다 — 샘플 제안 기능(agent/ai-brokered-samples-20261030)이 따로 가진다.
 */

/** 가능 공정 */
export const FACTORY_PROCESSES = [
  'mixing',
  'heat_sterilization',
  'filling',
  'pouch_packing',
  'bottle_packing',
  'can_packing',
  'powder',
  'freezing',
  'retort',
  'other',
] as const
export type FactoryProcess = (typeof FACTORY_PROCESSES)[number]

/** 최소 주문량을 나누는 포장 형태 */
export const FACTORY_PACK_FORMATS = [
  'pouch',
  'bottle',
  'can',
  'stick',
  'tray',
  'bulk',
  'other',
] as const
export type FactoryPackFormat = (typeof FACTORY_PACK_FORMATS)[number]

/**
 * 공장이 보는 말 (웹 폼·CSV 안내·관리자 화면). 공장용 양식이라 ko·en 만 둔다 (대표 지시 10/30: 최소 ko·en).
 * 그 밖의 언어는 en 을 쓴다
 */
export type FactoryFormLanguage = 'ko' | 'en'
export const FACTORY_PROCESS_LABELS: Record<FactoryFormLanguage, Record<FactoryProcess, string>> = {
  ko: {
    mixing: '배합',
    heat_sterilization: '가열 살균',
    filling: '충전',
    pouch_packing: '파우치 포장',
    bottle_packing: '병 포장',
    can_packing: '캔 포장',
    powder: '분말 가공',
    freezing: '냉동',
    retort: '레토르트',
    other: '기타',
  },
  en: {
    mixing: 'Mixing / blending',
    heat_sterilization: 'Heat sterilization',
    filling: 'Filling',
    pouch_packing: 'Pouch packing',
    bottle_packing: 'Bottle packing',
    can_packing: 'Can packing',
    powder: 'Powder processing',
    freezing: 'Freezing',
    retort: 'Retort',
    other: 'Other',
  },
}
export const FACTORY_PACK_FORMAT_LABELS: Record<
  FactoryFormLanguage,
  Record<FactoryPackFormat, string>
> = {
  ko: {
    pouch: '파우치',
    bottle: '병',
    can: '캔',
    stick: '스틱',
    tray: '트레이·용기',
    bulk: '벌크(대용량)',
    other: '기타',
  },
  en: {
    pouch: 'Pouch',
    bottle: 'Bottle',
    can: 'Can',
    stick: 'Stick',
    tray: 'Tray / cup',
    bulk: 'Bulk',
    other: 'Other',
  },
}

const text = (max: number) => z.string().trim().max(max)
const count = z.number().int().min(1).max(10_000_000)
const days = z.number().int().min(0).max(3650).nullable().default(null)

/** 설비 하나 (종류·용량·대수) */
export const factoryEquipmentSchema = z.object({
  kind: text(60).min(1),
  /** 예: '500L/배치', '시간당 3,000포' */
  capacity: text(60).default(''),
  units: z.number().int().min(1).max(999).default(1),
})

/** 공정별 단가 (단위·통화·기준 수량·유효기간) */
export const factoryProcessPriceSchema = z.object({
  process: z.enum(FACTORY_PROCESSES),
  /** 값의 단위 (kg, 포, 병, 배치 …) */
  unit: text(20).min(1),
  currency: z.enum(CURRENCIES),
  price: z.number().nonnegative().max(1e9),
  /** 이 값이 맞는 주문 수량 (unit 기준) */
  baseQuantity: count,
  /** 이 값의 유효기간 (YYYY-MM-DD). 모르면 null */
  validUntil: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
})

/** 포장 형태(와 분류)별 최소 주문량 */
export const factoryMoqSchema = z.object({
  packFormat: z.enum(FACTORY_PACK_FORMATS),
  /** 어느 제품 분류의 최소 주문량인지. 모든 분류면 null */
  categoryId: z.enum(PRODUCT_CATEGORY_IDS).nullable().default(null),
  quantity: count,
  unit: text(20).min(1),
})

/** 공장 설비·공정 틀. factories.capabilities 에 그대로 들어간다 */
export const factoryCapabilitiesSchema = z.object({
  equipment: z.array(factoryEquipmentSchema).max(30).default([]),
  processes: z.array(z.enum(FACTORY_PROCESSES)).max(FACTORY_PROCESSES.length).default([]),
  processPrices: z.array(factoryProcessPriceSchema).max(40).default([]),
  moqs: z.array(factoryMoqSchema).max(40).default([]),
  /** 시제품(개발) 기간, 일 */
  prototypeLeadTimeDays: days,
  /** 발주 확정부터 출고까지, 일 */
  productionLeadTimeDays: days,
  /** 소량 시제품을 만들 수 있는지. 모르면 null */
  smallBatchTrial: z.boolean().nullable().default(null),
  notes: text(1000).default(''),
})
export type FactoryCapabilities = z.infer<typeof factoryCapabilitiesSchema>

/**
 * 공장 회신 한 건 = 설비·공정 틀 + 인증. 인증의 원본은 공장 목록(factories.certifications)이라 반영할 때 그 칸을 바꾼다
 */
export const factoryReplySchema = factoryCapabilitiesSchema.extend({
  certifications: factoryInputSchema.shape.certifications,
})
export type FactoryReply = z.infer<typeof factoryReplySchema>

/** 회신 검토 상태. pending 은 관리자가 보기 전 — 운영 데이터(공장 목록)에 아직 들어가지 않았다 */
export const FACTORY_REPLY_STATUSES = ['pending', 'applied', 'rejected'] as const
export type FactoryReplyStatus = (typeof FACTORY_REPLY_STATUSES)[number]

/** 초대 토큰 꼴 (24바이트 난수의 base64url). API 와 공유 웹이 DB 를 보기 전에 같은 규칙으로 거른다 */
export const FACTORY_INVITE_TOKEN_RE = /^[A-Za-z0-9_-]{32}$/

/** POST /admin/factories/:id/reply-invites */
export const factoryReplyInviteInputSchema = z.object({
  language: z.enum(['ko', 'en']).default('ko'),
  /** 링크 유효 일수 */
  days: z.number().int().min(1).max(60).default(30),
})
export interface FactoryReplyInviteCreated {
  /** 공장에 보낼 주소. 토큰은 이때 한 번만 보인다(서버는 해시만 가진다) */
  url: string
  expiresAt: string
}

/** 초대 링크 일괄 만들기 한 번에 받는 공장 수 */
export const FACTORY_BULK_INVITE_MAX = 600

/** POST /admin/factory-reply-invites — 거른 메일 대상 공장마다 초대 링크 (대표 결정 10/30) */
export const factoryReplyBulkInviteInputSchema = factoryReplyInviteInputSchema.extend({
  /** D1 은 요청 하나에 쿼리 1,000개까지라 한 번에 600곳까지 (관리자 화면이 나눠 보낸다) */
  factoryIds: z.array(z.string().min(1).max(64)).min(1).max(FACTORY_BULK_INVITE_MAX),
})
export interface FactoryReplyBulkInviteResult {
  items: {
    factoryId: string
    name: string
    email: string
    contactFormUrl: string
    url: string
    expiresAt: string
  }[]
  /** 만들지 않은 공장. closed = 중단·연락 금지 */
  skipped: { factoryId: string; reason: 'not_found' | 'closed' }[]
}

/** GET /factory-replies/:token — 웹 폼 머리에 쓰는 것만 */
export interface FactoryReplyInviteView {
  factoryName: string
  language: FactoryFormLanguage
  expiresAt: string
}

/** GET /admin/factory-replies 의 한 줄 */
export interface AdminFactoryReplyView {
  id: string
  factoryId: string
  factoryName: string
  status: FactoryReplyStatus
  reply: FactoryReply
  createdAt: string
  reviewedBy: string | null
  reviewedAt: string | null
}

// ── 웹 폼 (apps/share /factory-reply/:token) ─────────────────────────────────

/** 웹 폼의 줄 수. 자바스크립트 없이 도는 폼이라 줄을 미리 그려 둔다 */
export const FACTORY_FORM_ROWS = { equipment: 5, processPrices: 6, moqs: 5 } as const

const num = (v: string) => {
  const s = v.replace(/[,\s]/g, '')
  return s === '' ? null : Number(s)
}

/**
 * 웹 폼 값 → 회신 (검사 전 값). 빈 줄은 뺀다. 숫자 칸의 쉼표는 지운다. 검사는 factoryReplySchema 가 한다
 */
export function factoryReplyFromForm(form: {
  get: (name: string) => string
  getAll: (name: string) => string[]
}): unknown {
  const rows = (n: number) => Array.from({ length: n }, (_, i) => i)
  const filled = (...names: string[]) => names.some((name) => form.get(name).trim() !== '')
  const trial = form.get('small_batch_trial')
  return {
    equipment: rows(FACTORY_FORM_ROWS.equipment)
      .filter((i) => filled(`eq_kind_${i}`, `eq_capacity_${i}`, `eq_units_${i}`))
      .map((i) => ({
        kind: form.get(`eq_kind_${i}`),
        capacity: form.get(`eq_capacity_${i}`),
        units: num(form.get(`eq_units_${i}`)) ?? 1,
      })),
    processes: form.getAll('process'),
    processPrices: rows(FACTORY_FORM_ROWS.processPrices)
      .filter((i) => filled(`pr_unit_${i}`, `pr_price_${i}`, `pr_base_${i}`, `pr_valid_${i}`))
      .map((i) => ({
        process: form.get(`pr_process_${i}`),
        unit: form.get(`pr_unit_${i}`),
        currency: form.get(`pr_currency_${i}`) || 'KRW',
        price: num(form.get(`pr_price_${i}`)),
        baseQuantity: num(form.get(`pr_base_${i}`)),
        validUntil: form.get(`pr_valid_${i}`) || null,
      })),
    moqs: rows(FACTORY_FORM_ROWS.moqs)
      .filter((i) => filled(`moq_qty_${i}`, `moq_unit_${i}`))
      .map((i) => ({
        packFormat: form.get(`moq_pack_${i}`),
        categoryId: form.get(`moq_category_${i}`) || null,
        quantity: num(form.get(`moq_qty_${i}`)),
        unit: form.get(`moq_unit_${i}`),
      })),
    prototypeLeadTimeDays: num(form.get('prototype_days')),
    productionLeadTimeDays: num(form.get('production_days')),
    smallBatchTrial: trial === 'yes' ? true : trial === 'no' ? false : null,
    certifications: form
      .get('certifications')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
    notes: form.get('notes'),
  }
}

// ── CSV 양식 (관리자 내려받기·가져오기) ──────────────────────────────────────

/**
 * 한 줄 = 항목 하나. section 이 무엇인지 정한다. '#' 로 시작하는 줄은 안내라 읽지 않는다.
 *   equipment       item=설비 종류, detail=용량, quantity=대수
 *   process         item=공정 코드
 *   price           item=공정 코드, unit, currency, price, base_quantity, valid_until
 *   moq             item=포장 형태 코드, detail=분류 코드(비우면 전체), quantity, unit
 *   lead_time       item=prototype|production, quantity=일
 *   certification   item=인증 이름
 *   small_batch_trial item=yes|no
 *   note            detail=메모
 */
export const FACTORY_CSV_COLUMNS = [
  'section',
  'item',
  'detail',
  'quantity',
  'unit',
  'currency',
  'price',
  'base_quantity',
  'valid_until',
] as const
type CsvRow = Record<(typeof FACTORY_CSV_COLUMNS)[number], string>

/**
 * CSV 양식 (UTF-8 BOM, 엑셀에서 바로 열린다). reply 가 있으면 그 값을 채워 내보낸다 — 지금 값을 받아 고쳐 다시 올리는 데 쓴다.
 * 안내 줄('#')은 ko·en 둘 다 넣는다
 */
export function factoryReplyToCsv(reply: FactoryReply | null): string {
  const guide = [
    '# RecipeCat 공장 설비·공정 양식 / factory capability form. # 로 시작하는 줄은 읽지 않습니다 / lines starting with # are ignored.',
    `# process: ${FACTORY_PROCESSES.map((p) => `${p}=${FACTORY_PROCESS_LABELS.ko[p]}`).join(' ')}`,
    `# moq item: ${FACTORY_PACK_FORMATS.map((p) => `${p}=${FACTORY_PACK_FORMAT_LABELS.ko[p]}`).join(' ')}`,
    `# moq detail (category, optional): ${PRODUCT_CATEGORY_IDS.join(' ')}`,
    `# currency: ${CURRENCIES.join(' ')} / valid_until: YYYY-MM-DD / lead_time item: prototype production (days) / small_batch_trial item: yes no`,
  ]
  const r = reply
  const rows: (string | number | null)[][] = r
    ? [
        ...r.equipment.map((e) => ['equipment', e.kind, e.capacity, e.units]),
        ...r.processes.map((p) => ['process', p]),
        ...r.processPrices.map((p) => [
          'price',
          p.process,
          '',
          '',
          p.unit,
          p.currency,
          p.price,
          p.baseQuantity,
          p.validUntil,
        ]),
        ...r.moqs.map((m) => ['moq', m.packFormat, m.categoryId, m.quantity, m.unit]),
        ...(r.prototypeLeadTimeDays === null
          ? []
          : [['lead_time', 'prototype', '', r.prototypeLeadTimeDays]]),
        ...(r.productionLeadTimeDays === null
          ? []
          : [['lead_time', 'production', '', r.productionLeadTimeDays]]),
        ...r.certifications.map((c) => ['certification', c]),
        ...(r.smallBatchTrial === null
          ? []
          : [['small_batch_trial', r.smallBatchTrial ? 'yes' : 'no']]),
        ...(r.notes ? [['note', '', r.notes]] : []),
      ]
    : [
        ['equipment', '배합 탱크', '500L', 2],
        ['process', 'mixing'],
        ['price', 'filling', '', '', 'EA', 'KRW', 120, 10000, '2026-12-31'],
        ['moq', 'pouch', 'sauce', 3000, 'EA'],
        ['lead_time', 'production', '', 21],
        ['certification', 'HACCP'],
        ['small_batch_trial', 'yes'],
      ]
  return csvDocument([...guide, csvLine([...FACTORY_CSV_COLUMNS]), ...rows.map(csvLine)])
}

export type FactoryCsvParse = { ok: true; reply: FactoryReply } | { ok: false; errors: string[] }

/** CSV 양식 → 회신. 줄 번호를 붙인 오류를 모두 모아 돌려준다 (하나라도 있으면 반영하지 않는다) */
export function parseFactoryReplyCsv(text: string): FactoryCsvParse {
  const lines = parseCsvRows(text)
    .map((cells, i) => ({ cells, line: i + 1 }))
    .filter(({ cells }) => !(cells[0] ?? '').trim().startsWith('#') && cells.some((c) => c.trim()))
  const [header, ...body] = lines
  const errors: string[] = []
  const cols = header?.cells.map((c) => c.trim().toLowerCase()) ?? []
  const missing = FACTORY_CSV_COLUMNS.filter((c) => !cols.includes(c))
  if (!header || missing.length)
    return {
      ok: false,
      errors: [`머리줄에 칸이 없습니다 / missing columns: ${missing.join(', ')}`],
    }
  const raw = {
    equipment: [] as unknown[],
    processes: [] as string[],
    processPrices: [] as unknown[],
    moqs: [] as unknown[],
    prototypeLeadTimeDays: null as number | null,
    productionLeadTimeDays: null as number | null,
    smallBatchTrial: null as boolean | null,
    certifications: [] as string[],
    notes: '',
  }
  for (const { cells, line } of body) {
    const row = Object.fromEntries(
      FACTORY_CSV_COLUMNS.map((c) => [c, (cells[cols.indexOf(c)] ?? '').trim()]),
    ) as CsvRow
    const n = (v: string) => num(v)
    switch (row.section.toLowerCase()) {
      case 'equipment':
        raw.equipment.push({ kind: row.item, capacity: row.detail, units: n(row.quantity) ?? 1 })
        break
      case 'process':
        raw.processes.push(row.item)
        break
      case 'price':
        raw.processPrices.push({
          process: row.item,
          unit: row.unit,
          currency: row.currency || 'KRW',
          price: n(row.price),
          baseQuantity: n(row.base_quantity),
          validUntil: row.valid_until || null,
        })
        break
      case 'moq':
        raw.moqs.push({
          packFormat: row.item,
          categoryId: row.detail || null,
          quantity: n(row.quantity),
          unit: row.unit,
        })
        break
      case 'lead_time':
        if (row.item === 'prototype') raw.prototypeLeadTimeDays = n(row.quantity)
        else if (row.item === 'production') raw.productionLeadTimeDays = n(row.quantity)
        else errors.push(`${line}줄: lead_time 의 item 은 prototype·production 중 하나`)
        break
      case 'certification':
        raw.certifications.push(row.item)
        break
      case 'small_batch_trial':
        if (row.item !== 'yes' && row.item !== 'no')
          errors.push(`${line}줄: small_batch_trial 의 item 은 yes·no 중 하나`)
        raw.smallBatchTrial = row.item === 'yes'
        break
      case 'note':
        raw.notes = [raw.notes, row.detail].filter(Boolean).join('\n')
        break
      default:
        errors.push(`${line}줄: 모르는 section '${row.section}'`)
    }
  }
  const parsed = factoryReplySchema.safeParse(raw)
  if (!parsed.success)
    errors.push(...parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`))
  return errors.length || !parsed.success ? { ok: false, errors } : { ok: true, reply: parsed.data }
}
